import { RouteObject } from 'react-router-dom';
import loadable from '@loadable/component';

import { ScreenerSettingName } from '@/hooks/screener-settings/screenerSettingName';
import { ExacareFeature, FeatureFlagService } from '@/utils/featureFlagService';

import { ProtectedRoute } from '../ProtectedRoute';
import { ScreenerSettingRouteGuard } from '../ScreenerSettingRouteGuard';

const AdminHub = loadable(() => import('@/pages/AdminHub')); // prettier-ignore
const AdminHubCareSettingsTabs = loadable(
  () => import('@/pages/AdminHub/CareSettings/CareSettingsTabs')
); // prettier-ignore
const AdminHubCommunitySettings = loadable(
  () => import('@/pages/AdminHub/CommunitySettings')
); // prettier-ignore
const AdminHubScreenerSettings = loadable(
  () => import('@/pages/AdminHub/ScreenerSettings')
); // prettier-ignore
const AdminHubScreenerSettingsUsageOverview = loadable(
  () => import('@/pages/AdminHub/ScreenerSettings/UsageOverview')
); // prettier-ignore
const AdminHubScreenerSettingsIntegrations = loadable(
  () => import('@/pages/AdminHub/ScreenerSettings/Integrations')
); // prettier-ignore
const AdminHubScreenerSettingsPayerList = loadable(
  () => import('@/pages/AdminHub/ScreenerSettings/PayerList')
); // prettier-ignore
const AdminHubScreenerSettingsOrganizationStates = loadable(
  () => import('@/pages/AdminHub/ScreenerSettings/OrganizationStates')
); // prettier-ignore
const AdminHubScreenerSettingsServiceArea = loadable(
  () => import('@/pages/AdminHub/ScreenerSettings/ServiceArea')
); // prettier-ignore
const AdminHubScreenerSettingsApprovedHospitalSources = loadable(
  () => import('@/pages/AdminHub/ScreenerSettings/ApprovedHospitalSources')
); // prettier-ignore
const AdminHubScreenerSettingsDocumentIgnoreList = loadable(
  () => import('@/pages/AdminHub/ScreenerSettings/DocumentIgnoreList')
); // prettier-ignore
const AdminHubScreenerSettingsCaseManagers = loadable(
  () => import('@/pages/AdminHub/ScreenerSettings/CaseManagers')
); // prettier-ignore
const AdminHubScreenerSettingsPayerContract = loadable(
  () =>
    import('@/pages/AdminHub/ScreenerSettings/PayerList/ContractDetails/index')
); // prettier-ignore
const AdminHubESignTemplates = loadable(
  () => import('@/pages/AdminHub/ESignTemplates')
); // prettier-ignore
const AdminHubESignTemplateCreateEdit = loadable(
  () => import('@/pages/AdminHub/ESignTemplates/ESignTemplateCreateEdit')
); // prettier-ignore
const AdminHubESignTemplatePreview = loadable(
  () => import('@/pages/AdminHub/ESignTemplates/ESignTemplatePreview')
); // prettier-ignore
const AdminHubWorkflowSettings = loadable(
  () => import('@/pages/AdminHub/WorkflowSettings')
); // prettier-ignore
const AdminHubWorkflowSettingsAllocationRules = loadable(
  () => import('@/pages/AdminHub/WorkflowSettings/AllocationRules')
); // prettier-ignore
const AdminHubClinicalRulesDataPulls = loadable(
  () => import('@/pages/AdminHub/ClinicalRulesDataPulls')
); // prettier-ignore

export enum AdminHubGeneralRoutes {
  AdminHub = '/:domain/admin-hub',
  AdminHubNotificationPreferences = '/:domain/admin-hub/notification-preferences',
  AdminHubCommunities = '/:domain/admin-hub/communities',
  AdminHubCommunity = '/:domain/admin-hub/community/:facility_id',
  AdminHubScreenerSettings = '/:domain/admin-hub/screener-settings',
  AdminHubScreenerSettingsUsageOverview = '/:domain/admin-hub/screener-settings/usage-overview',
  AdminHubScreenerSettingsIntegrations = '/:domain/admin-hub/screener-settings/integrations',
  AdminHubScreenerSettingsPayerList = '/:domain/admin-hub/screener-settings/payer-list',
  AdminHubScreenerSettingsPayerContract = '/:domain/admin-hub/screener-settings/payer-list/contracts/:contract_id',
  AdminHubScreenerSettingsOrganizationStates = '/:domain/admin-hub/screener-settings/organization-states',
  AdminHubScreenerSettingsServiceArea = '/:domain/admin-hub/screener-settings/service-area',
  AdminHubScreenerSettingsApprovedHospitalSources = '/:domain/admin-hub/screener-settings/approved-hospital-sources',
  AdminHubScreenerSettingsDocumentIgnoreList = '/:domain/admin-hub/screener-settings/document-ignore-list',
  AdminHubScreenerSettingsCaseManagers = '/:domain/admin-hub/screener-settings/case-managers',
  AdminHubESignTemplates = '/:domain/admin-hub/esign-templates',
  AdminHubESignTemplateCreate = '/:domain/admin-hub/esign-templates/create',
  AdminHubESignTemplateEdit = '/:domain/admin-hub/esign-templates/:template_id/edit',
  AdminHubESignTemplatePreview = '/:domain/admin-hub/esign-templates/:template_id',
  AdminHubWorkflowSettings = '/:domain/admin-hub/workflow-settings',
  AdminHubWorkflowSettingsAllocationRules = '/:domain/admin-hub/workflow-settings/allocation-rules',
  AdminHubClinicalRulesDataPulls = '/:domain/admin-hub/clinical-rules-data-pulls'
}

export const getAdminHubGeneralRoutes = (): RouteObject[] => {
  const screenerSettingsChildren: RouteObject[] = [
    {
      index: true,
      element: <AdminHubScreenerSettingsUsageOverview />
    },
    {
      path: AdminHubGeneralRoutes.AdminHubScreenerSettingsUsageOverview,
      element: <AdminHubScreenerSettingsUsageOverview />
    },
    {
      path: AdminHubGeneralRoutes.AdminHubScreenerSettingsIntegrations,
      element: <AdminHubScreenerSettingsIntegrations />
    },

    ...(FeatureFlagService.isEnabled(ExacareFeature.CRM_SCREENER_PAYER_LIST)
      ? [
          {
            path: AdminHubGeneralRoutes.AdminHubScreenerSettingsPayerList,
            element: <AdminHubScreenerSettingsPayerList />
          }
        ]
      : []),
    ...(FeatureFlagService.isEnabled(
      ExacareFeature.CRM_SCREENER_ORGANIZATION_STATES
    )
      ? [
          {
            path: AdminHubGeneralRoutes.AdminHubScreenerSettingsOrganizationStates,
            element: <AdminHubScreenerSettingsOrganizationStates />
          }
        ]
      : []),
    ...(FeatureFlagService.isEnabled(ExacareFeature.CRM_SCREENER_SERVICE_AREA)
      ? [
          {
            path: AdminHubGeneralRoutes.AdminHubScreenerSettingsServiceArea,
            element: <AdminHubScreenerSettingsServiceArea />
          }
        ]
      : []),

    {
      path: AdminHubGeneralRoutes.AdminHubScreenerSettingsApprovedHospitalSources,
      element: <AdminHubScreenerSettingsApprovedHospitalSources />
    },
    {
      path: AdminHubGeneralRoutes.AdminHubScreenerSettingsDocumentIgnoreList,
      element: <AdminHubScreenerSettingsDocumentIgnoreList />
    },
    {
      path: AdminHubGeneralRoutes.AdminHubScreenerSettingsCaseManagers,
      element: <AdminHubScreenerSettingsCaseManagers />
    }
  ];

  return [
    {
      path: AdminHubGeneralRoutes.AdminHub,
      element: <ProtectedRoute component={AdminHub} role="L1" />,
      children: [
        {
          path: AdminHubGeneralRoutes.AdminHubCommunities,
          element: <AdminHubCareSettingsTabs />
        },
        {
          path: AdminHubGeneralRoutes.AdminHubCommunity,
          element: <AdminHubCommunitySettings />
        },
        {
          path: AdminHubGeneralRoutes.AdminHubScreenerSettings,
          element: <AdminHubScreenerSettings />,
          children: screenerSettingsChildren
        },
        ...(FeatureFlagService.isEnabled(ExacareFeature.CRM_SCREENER_PAYER_LIST)
          ? [
              {
                path: AdminHubGeneralRoutes.AdminHubScreenerSettingsPayerContract,
                element: <AdminHubScreenerSettingsPayerContract />
              }
            ]
          : []),
        ...(FeatureFlagService.isEnabled(ExacareFeature.CRM_ESIGN)
          ? [
              {
                path: AdminHubGeneralRoutes.AdminHubESignTemplates,
                element: (
                  <ProtectedRoute
                    component={AdminHubESignTemplates}
                    role="L0.1"
                  />
                )
              },
              {
                path: AdminHubGeneralRoutes.AdminHubESignTemplateCreate,
                element: (
                  <ProtectedRoute
                    component={AdminHubESignTemplateCreateEdit}
                    role="L0.1"
                  />
                )
              },
              {
                path: AdminHubGeneralRoutes.AdminHubESignTemplateEdit,
                element: (
                  <ProtectedRoute
                    component={AdminHubESignTemplateCreateEdit}
                    role="L0.1"
                  />
                )
              },
              {
                path: AdminHubGeneralRoutes.AdminHubESignTemplatePreview,
                element: (
                  <ProtectedRoute
                    component={AdminHubESignTemplatePreview}
                    role="L0.1"
                  />
                )
              }
            ]
          : []),
        {
          path: AdminHubGeneralRoutes.AdminHubWorkflowSettings,
          element: (
            <ProtectedRoute component={AdminHubWorkflowSettings} role="L0" />
          ),
          children: [
            {
              index: true,
              element: <AdminHubWorkflowSettingsAllocationRules />
            },
            {
              path: AdminHubGeneralRoutes.AdminHubWorkflowSettingsAllocationRules,
              element: <AdminHubWorkflowSettingsAllocationRules />
            }
          ]
        },
        {
          path: AdminHubGeneralRoutes.AdminHubClinicalRulesDataPulls,
          element: (
            <ScreenerSettingRouteGuard
              settingName={ScreenerSettingName.FacTagCriteriaTargeting}
              requiredValue="enabled"
              redirectTo={AdminHubGeneralRoutes.AdminHub}
            >
              <AdminHubClinicalRulesDataPulls />
            </ScreenerSettingRouteGuard>
          )
        }
      ]
    }
  ];
};
